import { useState } from "react";
import { CalculatorPage } from "../../components/CalculatorPage";
import { ResultExplainer } from "../../components/ResultExplainer";
import { FilingStatusSelector } from "../../components/FilingStatusSelector";
import { ComparisonCard } from "../../components/ComparisonCard";
import { taxAPI, formatCurrency } from "../../lib/api";
import { buildCalculatorSchema } from "../../hooks/useSEO";
import { ComparisonChart } from "../../components/ComparisonChart";
import { EmailGatePdfButton } from "../../components/EmailGatePdfButton";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const ChildTaxCreditCalculator = () => {
  const [filing, setFiling] = useState("married_joint");
  const [children, setChildren] = useState("2");
  const [agi, setAgi] = useState("120000");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await taxAPI.childTaxCredit({
        num_children: Number(children),
        filing_status: filing,
        agi: Number(agi),
      });
      setResult(res);
    } catch (e) {
      toast.error("Calculation failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputPanel = (
    <div className="space-y-5">
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Filing Status</label>
        <FilingStatusSelector value={filing} onChange={setFiling} testid="ctc-filing" />
      </div>
      <div>
        <label htmlFor="ctc-kids" className="font-body text-sm font-semibold block mb-2">Qualifying Children</label>
        <input
          id="ctc-kids"
          type="number"
          inputMode="numeric"
          min="0"
          max="20"
          value={children}
          onChange={(e) => setChildren(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-children"
        />
      </div>
      <div>
        <label htmlFor="ctc-agi" className="font-body text-sm font-semibold block mb-2">Adjusted Gross Income (USD)</label>
        <input
          id="ctc-agi"
          type="number"
          inputMode="decimal"
          value={agi}
          onChange={(e) => setAgi(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-agi"
        />
      </div>
      <button
        onClick={run}
        disabled={loading}
        data-testid="calculate-btn"
        className="w-full bg-primary text-primary-foreground font-mono uppercase tracking-widest text-sm px-6 py-4 mt-3 hover:brightness-110 transition flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</> : "Calculate CTC"}
      </button>
    </div>
  );

  const resultPanel = result ? (
    <div className="space-y-6" data-testid="ctc-result">
      <div className="border-2 border-foreground bg-foreground text-background p-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background/60 mb-2 block">Bottom Line</span>
        <h3 className="font-heading text-2xl md:text-3xl font-bold leading-tight">
          Your family loses <span className="text-primary">{formatCurrency(result.loss)}</span> in credit value in 2026.
        </h3>
      </div>
      <ComparisonCard
        testid="ctc-comparison"
        rows={[
          { label: "Credit per Child", value2025: result.year_2025.credit_per_child, value2026: result.year_2026.credit_per_child, format: "currency", invertImpact: true },
          { label: "Total Credit", value2025: result.year_2025.total_credit, value2026: result.year_2026.total_credit, format: "currency", invertImpact: true },
          { label: "Refundable Max / Child", value2025: result.year_2025.refundable_max_per_child, value2026: result.year_2026.refundable_max_per_child, format: "currency", invertImpact: true },
          { label: "Phase-Out Starts At", value2025: result.year_2025.phaseout_threshold, value2026: result.year_2026.phaseout_threshold, format: "currency", invertImpact: true },
        ]}
      />

      <ComparisonChart
        testid="ctc-chart"
        title="Child Tax Credit: 2025 vs 2026"
        rows={[
          { label: "Per Child", v2025: result.year_2025.credit_per_child, v2026: result.year_2026.credit_per_child },
          { label: "Total", v2025: result.year_2025.total_credit, v2026: result.year_2026.total_credit },
          { label: "Refundable", v2025: result.year_2025.refundable_max_per_child, v2026: result.year_2026.refundable_max_per_child },
        ]}
      />

      <EmailGatePdfButton
        testid="ctc-pdf"
        filename="taxshift-2026-child-tax-credit.pdf"
        pdfPayload={{
          title: "2026 Child Tax Credit Analysis",
          subtitle: `${result.num_children} qualifying children · ${result.filing_status.replace("_", " ").toUpperCase()}`,
          inputs: [
            { label: "Qualifying Children", value: result.num_children },
            { label: "Filing Status", value: result.filing_status.replace("_", " ").toUpperCase() },
            { label: "AGI", value: formatCurrency(result.agi) },
          ],
          headline: `Your family loses ${formatCurrency(result.loss)} in credit value in 2026.`,
          rows: [
            { label: "Credit per Child", v2025: formatCurrency(result.year_2025.credit_per_child), v2026: formatCurrency(result.year_2026.credit_per_child) },
            { label: "Total Credit", v2025: formatCurrency(result.year_2025.total_credit), v2026: formatCurrency(result.year_2026.total_credit) },
            { label: "Refundable Max / Child", v2025: formatCurrency(result.year_2025.refundable_max_per_child), v2026: formatCurrency(result.year_2026.refundable_max_per_child) },
            { label: "Phase-Out Starts", v2025: formatCurrency(result.year_2025.phaseout_threshold), v2026: formatCurrency(result.year_2026.phaseout_threshold) },
          ],
        }}
      />
      <ResultExplainer slug="child-tax-credit-2026" />
    </div>
  ) : (
    <div className="border-2 border-dashed border-foreground/30 p-12 text-center bg-card/50" data-testid="ctc-empty">
      <p className="font-heading text-xl">Enter your family's details and click Calculate.</p>
    </div>
  );

  return (
    <CalculatorPage
      title="2026 Child Tax Credit Calculator (OBBBA)"
      slug="child-tax-credit-2026"
      overline="Calculator · Credits · Families"
      description="OBBBA made the Child Tax Credit permanent at $2,200 per qualifying child (up from $2,000), inflation-indexed going forward. Phase-outs begin at $200k single / $400k joint AGI. Calculate your family's exact credit."
      seoTitle="2026 Child Tax Credit Calculator — OBBBA $2,200"
      seoKeywords="child tax credit 2026, obbba ctc, 2200 ctc, dependents credit 2026, family tax credit 2026"
      jsonLd={buildCalculatorSchema({
        name: "2026 Child Tax Credit Calculator",
        description: "Free calculator showing how the Child Tax Credit works under OBBBA — now permanent at $2,200 per child.",
        url: "/calculator/child-tax-credit-2026",
      })}
      inputPanel={inputPanel}
      resultPanel={resultPanel}
      education={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">
            $2,200 per child — now permanent
          </h2>
          <div className="font-body text-base leading-relaxed space-y-4 text-foreground/85">
            <p>
              OBBBA made the expanded Child Tax Credit permanent and raised it to $2,200 per qualifying
              child under 17 — up from the TCJA-era $2,000. The $1,700 refundable portion is also
              inflation-indexed going forward.
            </p>
            <p>
              The generous phase-out thresholds are preserved: $200,000 single / $400,000 joint.
              This is dramatically more generous than the pre-TCJA $75k/$110k phase-outs that would
              have returned in 2026 had OBBBA not passed.
            </p>
            <p>
              OBBBA added a new requirement: both the qualifying child AND at least one filing parent
              must have a valid Social Security Number (not an ITIN) to claim the CTC.
            </p>
          </div>
        </>
      }
      relatedSlugs={[
        { slug: "tax-bracket-2026", label: "Tax Bracket" },
        { slug: "standard-deduction-2026", label: "Standard Deduction" },
      ]}
    />
  );
};

export default ChildTaxCreditCalculator;
